import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { SideNav } from "components";
import {
  BaseDropdown,
  BaseInput,
  FileUpload,
  LargeButton,
  MainBody,
  OverlayLoader,
  PageTitle,
  Ptag,
} from "components/core";
import http from "services/httpService";
import useFlex from "hooks/useFlex";
import { Formik, Form } from "formik";
import {
  gpaGtlInitialValues,
  gpaGtlSchema,
} from "components/PlanSetup/schemas";
import { rupeesToNumber, updateFlexUserPlanIds, uploadFile } from "utlis/utils";
import { DividerOr, Divv } from "components/PlanSetup/styles";
import { Divider } from "@mui/material";
import UploadFile from "components/UploadFile";
import {
  AMOUNT_PER_MIL_MULTIPLIERS,
  AMOUNT_PER_MIL_MULTIPLIERS_ARRAY,
} from "../constants";
import BaseTickBoxes from "components/Flex/BaseTickBoxes";
import AttributesTable from "components/Flex/AttributesTable";

const FlexGpaSetup = () => {
  const { state } = useLocation();
  const { getProductDetails, getProductType, next } = useFlex();

  const [attrDescriptionError, setAttrDescriptionError] = useState([]);
  const [multiplier, setMultiplier] = useState();
  const [file, setFile] = useState();
  const [fileError, setFileError] = useState({
    productError: false,
    documentError: false,
  });
  const [attributes, setAttributes] = useState();
  const [documentId, setDocumentId] = useState();
  const productDetails = useMemo(() => getProductDetails(state.product), [state]);
  const [setupDetails, setSetupDetails] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAttributes, setSelectedAttributeArray] = useState();
  const [dependentSensitive, setDependentSensitive] = useState();
  // fetch GPA setup details
  const fetchSetupDetails = async () => {
    const URL = "/business/get_gpa_plan_setup_details/";
    try {
      setIsLoading(true);
      const { data } = await http.get(URL);
      setSetupDetails(data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async function () {
      await fetchSetupDetails();
    })();
  }, [state.key]);

  const [options] = JSON.parse(localStorage.getItem("completed_product_list"))
    .filter((item) => item.product === productDetails.value)
    .map((item) => item.option);

  const title = useMemo(() => {
    return `Setup ${productDetails.display_name}`;
  }, [productDetails]);

  const setupGpaGtl = async (
    premium_id,
    // { insurer: insurer_name, sum_insured },
    { insurer: insurer_name, sum_insured, commission, amount_per_mil, attribute_definition_mapping }
  ) => {
    // getting business user id
    const business_user_id = localStorage.getItem("id");
    if (sum_insured == "FLAT") {
      sum_insured = rupeesToNumber(commission);
    }
    if (amount_per_mil) {
      amount_per_mil = rupeesToNumber(amount_per_mil);
    }
    try {
      const [options] = JSON.parse(
        localStorage.getItem("completed_product_list")
      )
        .filter((item) => item.product === productDetails.value)
        .map((item) => item.option);
      const rate_chart_id = file && (await uploadFile(file, "premiums_excel"));
      let selected = {}, display = {};
      attributes && selectedAttributes && attributes.forEach((item, index) => {
        if(selectedAttributes[item] === 0){
          selected[item]=true;
          display[item]=true
        }else if(selectedAttributes[item] === 1){
          selected[item]=false;
          display[item]=true
        }else{
          selected[item]=false;
          display[item]=false
        }
      });
      const trueKeys = multiplier && Object.entries(multiplier)
        .filter(([key, value]) => value === true)
        .map(([key, value]) => Number(key))
        .sort();
      let { data } = await http.post("/business/setupplan/", {
        business_type: productDetails.value,
        business_user_id,
        insurer_name,
        rate_chart_id,
        sum_insured,
        employee_policy_document_id: documentId,
        amount_per_mil,
        sum_insured_multipliers: trueKeys,
        employee_visibility_mapping: display,
        employee_selectable_mapping: selected,
        dependent_sensitive_mapping : dependentSensitive,
        attribute_definition_mapping,
        mandatory: options == 0 ? true : false,
      });

      updateFlexUserPlanIds(productDetails.value, data.userplan_id);
      localStorage.setItem("flex_userplan_id", data.userplan_id);
      setFile();
      setAttributes();
      next();
    } catch (err) {
      alert(err.response.data.error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isVoluntary = useMemo(() => {
    return getProductType(state.product) == 1;
  });

  const getOptions = (details) => {
    if (!details) return [];
    return details.map(({ display_name, value }) => ({
      label: display_name,
      value,
    }));
  };

  const handleAttributeDescriptionErrors = (values) => {
    const errors = [];
    if (!attributes) return false;
    attributes.forEach((item, index) => {
      if (
        (selectedAttributes[item] === 0 || selectedAttributes[item] === 1) &&
        !values.attribute_definition_mapping[item]
      ) {
        errors.push(item);
      }
    });
    setAttrDescriptionError(errors);
    return errors.length > 0;
  };

  // handle form submission
  const handleSubmit = async (
    values,
    { setSubmitting, setTouched, resetForm }
  ) => {
    const isError = handleAttributeDescriptionErrors(values);
    // if(isError) return;
    // exiting if no files
    if (!file && !values.amount_per_mil) {
      alert("Please Enter Amount Per Mil or Upload Rate Chart");
      setFileError((prev) => ({
        ...prev,
        file: true
      }))
      return;
    }
    if(!documentId) {
      setFileError((prev) => ({
        ...prev,
        document: true
      }))
      return;
    }
    // setting touched to true to display errors if errors persist
    setTouched(
      Object.keys(values).reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {})
    );
    // uploading file and storing the file_id
    setIsLoading(true);
    const resp = await setupGpaGtl(null, values);
    setSubmitting(false);
  };

  const uploadedFileValues = (values, file) => {
    setAttributes(values);
    setFile(file);
  };

  return (
    <section>
      {isLoading ? (
        <OverlayLoader />
      ) : (
        <>
          <SideNav />
          <MainBody style={{
            position: "relative",
            minHeight: "100vh",
            paddingBottom: "15rem",
          }}>
            <PageTitle title={title} />
            <div style={{ maxWidth: "800px" }}>
              <Formik
                enableReinitialize={true}
                initialValues={gpaGtlInitialValues}
                validationSchema={gpaGtlSchema}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <Form>
                    <div style={{ marginBottom: "2.2rem" }}>
                      <BaseDropdown
                        fontSize="14"
                        label="Insurer"
                        options={getOptions(setupDetails["insurers"])}
                        formikProps={{ ...formikProps, name: "insurer" }}
                      />
                    </div>
                    <div style={{ marginBottom: "2.2rem" }}>
                      <BaseDropdown
                        fontSize="14"
                        label="Sum Insured"
                        options={getOptions(setupDetails["sum_insured"])}
                        formikProps={{
                          ...formikProps,
                          name: "sum_insured",
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: "2.2rem", marginTop: "2rem" }}>
                      {formikProps &&
                        formikProps.values &&
                        formikProps.values.sum_insured == "FLAT" && (
                          <BaseInput
                            label="Sum Insured Amount"
                            type="text"
                            formatToRupee
                            formikProps={{
                              ...formikProps,
                              name: "commission",
                            }}
                          />
                        )}
                    </div>
                    <UploadFile
                      label="Upload Benefit Manual"
                      text="Upload PDF"
                      format="pdf"
                      type="policy_document"
                      onSelect={setDocumentId}
                      showError={fileError.document}
                    />
                    <div style={{ margin: "4rem 0rem 0rem 0rem" }}>
                      <Divider />
                    </div>
                    <UploadFile
                      label="Upload Rate Chart"
                      text="Upload Excel"
                      format="excel"
                      customApi="/business/get_product_attributes/"
                      customData={uploadedFileValues}
                      sampleFile="CdLedger"
                      showError={fileError.file}
                      isDisabled={formikProps.values.amount_per_mil}
                    />
                    {!!attributes && (
                      <AttributesTable
                      attrDescriptionError={attrDescriptionError}
                      attributes={attributes}
                      formikProps={formikProps}
                      onAttrChange={setSelectedAttributeArray}
                      dependentSensitive={setDependentSensitive}
                    />
                    )}
                    {!isVoluntary && (
                      <>
                        <div style={{ marginBottom: "30px" }}>
                          <Divv>
                            <DividerOr>or</DividerOr>
                          </Divv>
                        </div>
                        <Ptag
                          fontSize="20px"
                          fontWeight="500"
                          letterHeight="19px"
                          marginBottom="20px"
                        >
                          Enter Amount
                        </Ptag>
                        {/* <FooterInputWrapper> */}
                        <BaseInput
                          label="Enter Amount Per Mil"
                          type="text"
                          disabled={!!file}
                          formatToRupee
                          formikProps={{
                            ...formikProps,
                            name: "amount_per_mil",
                          }}
                        />
                        <Ptag
                          fontSize="14px"
                          fontWeight="500"
                          letterHeight="19px"
                          marginTop="30px"
                        >
                          Select Sum Insured multiples that can be upgraded by employees
                        </Ptag>
                        <BaseTickBoxes
                          attributes={AMOUNT_PER_MIL_MULTIPLIERS}
                          onChange={setMultiplier}
                          disabled={!!file}
                        />
                      </>
                    )}

                    {/* <BaseDropdown
                        fontSize="14"
                        disabled={!!file}
                        label="Multiplier"
                        options={AMOUNT_PER_MIL_MULTIPLIERS}
                        multiple
                        formikProps={{ ...formikProps, name: "sum_insured_multipliers" }}
                      /> */}
                    {/* </FooterInputWrapper> */}
                    <div style={{ 
                      bottom: "40px",
                      position: "absolute",
                      width: "calc(100% - 8rem)",
                      maxWidth: "800px",
                     }}>
                      <LargeButton
                        type="submit"
                        label="Save & Next"
                        marginTop="2rem"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </MainBody>
        </>
      )}
    </section>
  );
};

export default FlexGpaSetup;
